import React, { useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { actionAtom } from 'atoms/atoms'
import { useAtomValue } from 'jotai'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { Box, Button, Input, toast } from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { DurationInMilliseconds } from 'constants/General'
import { getPasswordSecret } from 'services/ApiKeysService'
import { changePassword } from 'services/UserDetailsService'

const passwordSchema = yup.object({
  currentPassword: yup.string().required('Current password is required'),
  newPassword: yup
    .string()
    .required('New password is required')
    .matches(
      /^(?=.*[A-Z])(?=.*[0-9]).{8,}$/,
      'Password must be at least 8 characters long and contain at least one uppercase letter and one number'
    ),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('newPassword')], 'Passwords must match'),
})

type PasswordFormData = yup.InferType<typeof passwordSchema>

export const ChangePasswordForm = () => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const action = useAtomValue(actionAtom)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    setError,
  } = useForm<PasswordFormData>({
    mode: 'onChange',
    resolver: yupResolver(passwordSchema),
  })

  const handleSubmitPassword = async ({
    currentPassword,
    confirmPassword,
    newPassword,
  }: PasswordFormData) => {
    try {
      setIsLoading(true)
      const res = await getPasswordSecret(currentPassword)
      if (res?.body) {
        await changePassword(currentPassword, newPassword, confirmPassword)
        toast({ header: t('passwordSuccess'), variant: 'success' })
        handleClose()
      } else {
        setError('currentPassword', { message: t('incorrectPassword') })
      }
    } catch (error) {
      const {
        data: { errorList },
      } = error
      const errorMessage = `${errorList?.[0]?.message}`
      errorMessage &&
        toast({
          header: errorMessage,
          variant: 'error',
          duration: DurationInMilliseconds.TWENTY_SECONDS,
        })
      logSentryError(
        `Error from ChangePasswordModal - submitPasswordChange - ${errorMessage}`,
        error
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    reset()
    action?.onClose()
  }

  return (
    <Box
      tag="form"
      flexCol
      gapY={16}
      paddingT={24}
      className="w-full"
      onSubmit={handleSubmit(handleSubmitPassword)}
    >
      <Input
        type="password"
        testId="current-password-input"
        label={t('currentPassword')}
        placeholder={t('currentPasswordPlaceholder')}
        error={!!errors.currentPassword}
        errorText={errors.currentPassword?.message}
        {...register('currentPassword')}
        onChange={e => {
          setValue('currentPassword', e.target.value, { shouldValidate: true })
        }}
      />
      <Input
        type="password"
        testId="new-password-input"
        label={t('newPasswordSimple')}
        placeholder={t('newPasswordPlaceholder')}
        error={!!errors.newPassword}
        errorText={errors.newPassword?.message}
        {...register('newPassword')}
        onChange={e => {
          setValue('newPassword', e.target.value, { shouldValidate: true })
        }}
      />
      <Input
        type="password"
        testId="confirm-password-input"
        label={t('confirmPassword')}
        placeholder={t('confirmPasswordPlaceholder')}
        error={!!errors.confirmPassword}
        errorText={errors.confirmPassword?.message}
        {...register('confirmPassword')}
        onChange={e => {
          setValue('confirmPassword', e.target.value, { shouldValidate: true })
        }}
      />

      <Box flex gapX={16} paddingT={8} justify="between">
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          {t('cancel')}
        </Button>

        <Button variant="primary" type="submit" loading={isLoading}>
          {t('save')}
        </Button>
      </Box>
    </Box>
  )
}
